import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, Lock, User, Building, Phone, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, register, isAuthenticated, initiateGoogleLogin } = useAuth();
    const redirect = new URLSearchParams(location.search).get('redirect') || '/dashboard';
    const [loading, setLoading] = useState(false);
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [registerData, setRegisterData] = useState({
        email: '',
        password: '',
        name: '',
        business_name: '',
        phone: '',
        is_b2b: false
    });

    useEffect(() => {
        if (isAuthenticated) {
            navigate(redirect);
        }
    }, [isAuthenticated, navigate, redirect]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const error = params.get('error');
        if (error) {
            const messages = {
                google_not_configured: 'Google sign-in is not configured yet.',
                google_auth_failed: 'Google sign-in was cancelled or failed.',
                token_exchange_failed: 'Could not complete Google sign-in. Please try again.',
                userinfo_failed: 'Could not retrieve your Google profile.',
                no_email: 'No email was returned from Google.'
            };
            toast.error(messages[error] || 'Google sign-in failed.');
            navigate('/login', { replace: true });
        }
    }, [location.search, navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(loginData.email, loginData.password);
            toast.success('Welcome back!');
            navigate(redirect);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register(registerData);
            toast.success("Account created! We've sent a verification email to confirm your address.");
            navigate(redirect);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = () => {
        initiateGoogleLogin(redirect);
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA] flex">
            {/* Left Panel - Form */}
            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-block">
                            <img 
                                src="/logo.png" 
                                alt="Henry's Aussie Fruit & Veggies" 
                                className="h-24 w-auto mx-auto"
                            />
                        </Link>
                        <p className="text-slate-500 mt-2">Fresh • Local • Quality</p>
                    </div>

                    {redirect !== '/dashboard' && (
                        <div className="mb-6 rounded-xl bg-[#1B4D3E]/5 border border-[#1B4D3E]/20 px-4 py-3 text-center text-sm text-[#1B4D3E] font-medium" data-testid="login-gate-prompt">
                            Please sign in to shop and view wholesale prices.
                        </div>
                    )}

                    <div className="bg-white rounded-2xl shadow-soft p-6 md:p-8">
                        <Tabs defaultValue="login" className="space-y-6">
                            <TabsList className="grid grid-cols-2 w-full">
                                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                            </TabsList>

                            {/* Login Tab */}
                            <TabsContent value="login">
                                <form onSubmit={handleLogin} className="space-y-4">
                                    <div>
                                        <Label htmlFor="login-email">Email</Label>
                                        <div className="relative mt-1">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="login-email"
                                                type="email"
                                                value={loginData.email}
                                                onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                                                placeholder="you@example.com"
                                                className="pl-10"
                                                required
                                                data-testid="login-email-input"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label htmlFor="login-password">Password</Label>
                                        <div className="relative mt-1">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="login-password"
                                                type="password"
                                                value={loginData.password}
                                                onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                                                placeholder="••••••••"
                                                className="pl-10"
                                                required
                                                data-testid="login-password-input"
                                            />
                                        </div>
                                    </div>
                                    <Button 
                                        type="submit" 
                                        className="btn-primary w-full py-3 h-auto"
                                        disabled={loading}
                                        data-testid="login-submit-btn"
                                    >
                                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Login'}
                                    </Button>
                                </form>

                                <div className="relative my-6">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-slate-200" />
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-white text-slate-500">or continue with</span>
                                    </div>
                                </div>

                                <Button 
                                    type="button"
                                    variant="outline"
                                    className="w-full py-3 h-auto"
                                    onClick={handleGoogleLogin}
                                    data-testid="google-login-btn"
                                >
                                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                    </svg>
                                    Continue with Google
                                </Button>
                            </TabsContent>

                            {/* Register Tab */}
                            <TabsContent value="register">
                                <form onSubmit={handleRegister} className="space-y-4">
                                    <div>
                                        <Label htmlFor="register-name">Full Name</Label>
                                        <div className="relative mt-1">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="register-name"
                                                type="text"
                                                value={registerData.name}
                                                onChange={(e) => setRegisterData(prev => ({ ...prev, name: e.target.value }))}
                                                placeholder="John Doe"
                                                className="pl-10"
                                                required
                                                data-testid="register-name-input"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label htmlFor="register-email">Email</Label>
                                        <div className="relative mt-1">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="register-email"
                                                type="email"
                                                value={registerData.email}
                                                onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                                                placeholder="you@example.com"
                                                className="pl-10"
                                                required
                                                data-testid="register-email-input"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label htmlFor="register-password">Password</Label>
                                        <div className="relative mt-1">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="register-password"
                                                type="password"
                                                value={registerData.password}
                                                onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                                                placeholder="••••••••"
                                                className="pl-10"
                                                required
                                                data-testid="register-password-input"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
                                        <Checkbox 
                                            id="is_b2b"
                                            checked={registerData.is_b2b}
                                            onCheckedChange={(checked) => setRegisterData(prev => ({ ...prev, is_b2b: checked }))}
                                            data-testid="is-b2b-checkbox"
                                        />
                                        <Label htmlFor="is_b2b" className="text-sm cursor-pointer">
                                            I'm a business (restaurant, retailer, etc.)
                                        </Label>
                                    </div>

                                    {registerData.is_b2b && (
                                        <div className="space-y-4 animate-fadeIn">
                                            <div>
                                                <Label htmlFor="business_name">Business Name</Label>
                                                <div className="relative mt-1">
                                                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                                    <Input
                                                        id="business_name"
                                                        type="text"
                                                        value={registerData.business_name}
                                                        onChange={(e) => setRegisterData(prev => ({ ...prev, business_name: e.target.value }))}
                                                        placeholder="Your Business Name"
                                                        className="pl-10"
                                                        data-testid="business-name-input"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <Label htmlFor="phone">Phone Number</Label>
                                                <div className="relative mt-1">
                                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                                    <Input
                                                        id="phone"
                                                        type="tel"
                                                        value={registerData.phone}
                                                        onChange={(e) => setRegisterData(prev => ({ ...prev, phone: e.target.value }))}
                                                        placeholder="0400 000 000"
                                                        className="pl-10"
                                                        data-testid="phone-input"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <Button 
                                        type="submit" 
                                        className="btn-primary w-full py-3 h-auto"
                                        disabled={loading}
                                        data-testid="register-submit-btn"
                                    >
                                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
                                    </Button>
                                </form>

                                <div className="relative my-6">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-slate-200" />
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-white text-slate-500">or continue with</span>
                                    </div>
                                </div>

                                <Button 
                                    type="button"
                                    variant="outline"
                                    className="w-full py-3 h-auto"
                                    onClick={handleGoogleLogin}
                                    data-testid="google-register-btn"
                                >
                                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                    </svg>
                                    Continue with Google
                                </Button>
                            </TabsContent>
                        </Tabs>
                    </div>

                    <p className="text-center text-sm text-slate-500 mt-6">
                        New here? Create an account above to browse our wholesale range and prices.
                    </p>
                </div>
            </div>

            {/* Right Panel - Image (Desktop only) */}
            <div className="hidden lg:block lg:w-1/2 relative">
                <img 
                    src="https://images.unsplash.com/photo-1598962099619-528a224cb625?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Fresh produce"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#1B4D3E]/80" />
                <div className="absolute inset-0 flex items-center justify-center p-12">
                    <div className="text-center text-white">
                        <h2 className="text-4xl font-bold mb-4">Fresh from the farm</h2>
                        <p className="text-xl text-white/80 max-w-md">
                            Join hundreds of restaurants and families who trust us for their produce needs.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
